import moment from "moment";
import { coreApi } from "./calls";
import { GetFilenameFromResponse } from "../helpers";

/**
 * Default rule configuration for weekly schedules
 */
const getDefaultRule = () => [{
  name: "1x WEEKLY",
  date_from: moment(),
  rrule: "WEEKLY"
}];

/**
 * Extract weekly rules from building unit groups data
 * @param {Array} data - Array of building unit groups
 * @returns {Array} - Filtered and mapped weekly rules
 */
const extractWeeklyRules = (data) => {
  return data.filter(rule => rule.rrule_blueprint.includes("WEEKLY"))
    .slice(0, 1)
    .map(rule => ({
      ...rule,
      rrule: rule.rrule_blueprint,
      date_from: moment().day(moment().day() >= 1 ? 1 : -6)
    }));
};

/**
 * Fetch building unit schedule rules
 * @param {string} buildingUnitId - Building unit ID
 * @returns {Promise<Array>} - Promise resolving to array of rules
 */
export const fetchBuildingUnitScheduleRules = async (buildingUnitId) => {
  try {
    const response = await coreApi.get(`buildings/units/groups/${buildingUnitId}`);
    const weeklyRules = extractWeeklyRules(response.data);

    return weeklyRules.length > 0 ? weeklyRules : getDefaultRule();
  } catch (error) {
    console.error('Failed to fetch building unit groups:', error);
    // Return default rules on error
    return getDefaultRule();
  }
};

/**
 * Download cleaning schedule PDF
 * @param {Object} params - Parameters for PDF generation
 * @param {string} params.buildingUnitId - Building unit ID
 * @param {string} params.dateFrom - Start date in YYYY-MM-DD format
 * @param {boolean} params.freeGeneralCleaning - Include free general cleaning
 * @param {string} params.fileName - Fallback file name for download
 * @returns {Promise} - Promise resolving with data and filename from response or fallback
 */
export const downloadCleaningSchedulePDF = async ({
  buildingUnitId,
  dateFrom,
  freeGeneralCleaning,
  fileName
}) => {
  const response = await coreApi.get("/cleaning-schedule", {
    responseType: "blob",
    params: {
      building_unit_id: buildingUnitId,
      cleaning_date_start: dateFrom,
      free_general_cleaning: freeGeneralCleaning,
    },
  });

  const filenameFromHeader = GetFilenameFromResponse(response);
  
  return { 
    data: response.data, 
    fileName: filenameFromHeader || fileName 
  };
};
